import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from '@happyspace/sdk/data';
import { usePreviewSafeData } from "../hooks/usePreviewSafeData";
import {
  LayoutDashboard,
  Briefcase,
  Calendar,
  Mail,
  Users,
  LogOut,
  CheckCircle,
  XCircle,
  Trash2,
  Eye,
  Search,
  Tag,
  Sparkles,
  Lock,
  Shield,
  Clock,
  MapPin,
  ExternalLink,
  Plus,
  ChevronRight,
  Filter,
  GripVertical,
  Download,
  Star,
  Layers,
  Pencil,
  RotateCcw,
  EyeOff,
} from "lucide-react";
import RestaurantCSVExport from "./RestaurantCSVExport";
import { useAdmin } from "../context/AdminContext";
import { useDragSort } from "../hooks/useDragSort";
import { DEFAULT_CATEGORIES, normalizeCategoryName } from "../data/categories";
import CategoriesTab from "./CategoriesTab";

export default function Admin() {
  const navigate = useNavigate();
  const { isAdmin, logout } = useAdmin();
  const [activeTab, setActiveTab] = useState("overview");

  // Redirect unauthenticated users to the login screen.
  useEffect(() => {
    if (!isAdmin) navigate("/admin/login", { replace: true });
  }, [isAdmin, navigate]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="w-full md:w-64 bg-[#082f6d] text-white shrink-0 flex flex-col">
        {/* Brand */}
        <div className="p-6 border-b border-blue-900/50 flex items-center gap-3">
          <div className="bg-[#f59e0b] p-1.5 rounded-lg text-white">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-bold text-lg leading-tight">Admin Console</h1>
            <p className="text-xs text-blue-200">Dallas Desi Scoop</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1.5">
          <SidebarLink
            icon={<LayoutDashboard className="w-[18px] h-[18px]" />}
            label="Overview"
            active={activeTab === "overview"}
            onClick={() => setActiveTab("overview")}
          />
          <SidebarLink
            icon={<Briefcase className="w-[18px] h-[18px]" />}
            label="Business Listings"
            active={activeTab === "businesses"}
            onClick={() => setActiveTab("businesses")}
          />
          <SidebarLink
            icon={<Calendar className="w-[18px] h-[18px]" />}
            label="Event Listings"
            active={activeTab === "events"}
            onClick={() => setActiveTab("events")}
          />
          <SidebarLink
            icon={<Mail className="w-[18px] h-[18px]" />}
            label="Submissions"
            active={activeTab === "submissions"}
            onClick={() => setActiveTab("submissions")}
          />
          <SidebarLink
            icon={<Users className="w-[18px] h-[18px]" />}
            label="Subscribers"
            active={activeTab === "subscribers"}
            onClick={() => setActiveTab("subscribers")}
          />
          <SidebarLink
            icon={<Tag className="w-[18px] h-[18px]" />}
            label="Deals"
            active={activeTab === "deals"}
            onClick={() => setActiveTab("deals")}
          />
          <SidebarLink
            icon={<Sparkles className="w-[18px] h-[18px]" />}
            label="Experiences"
            active={activeTab === "experiences"}
            onClick={() => setActiveTab("experiences")}
          />
          <SidebarLink
            icon={<Star className="w-[18px] h-[18px]" />}
            label="Influencers"
            active={activeTab === "influencers"}
            onClick={() => setActiveTab("influencers")}
          />
          <SidebarLink
            icon={<Star className="w-[18px] h-[18px]" />}
            label="Models"
            active={activeTab === "models"}
            onClick={() => setActiveTab("models")}
          />
          <SidebarLink
            icon={<Briefcase className="w-[18px] h-[18px]" />}
            label="Jobs"
            active={activeTab === "jobs"}
            onClick={() => setActiveTab("jobs")}
          />
          <SidebarLink
            icon={<Layers className="w-[18px] h-[18px]" />}
            label="Categories"
            active={activeTab === "categories"}
            onClick={() => setActiveTab("categories")}
          />
          <SidebarLink
            icon={<Download className="w-[18px] h-[18px]" />}
            label="Export Data"
            active={activeTab === "export"}
            onClick={() => setActiveTab("export")}
          />
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-blue-900/50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium text-blue-200 hover:text-white hover:bg-blue-900/30 transition-all duration-200"
          >
            <span className="flex items-center gap-2">
              <LogOut className="w-4 h-4" />
              Sign Out
            </span>
            <ChevronRight className="w-4 h-4 text-blue-300" />
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto min-h-screen">
        <header className="bg-white border-b border-gray-200 h-16 px-6 md:px-8 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Admin Control Panel</span>
            <span>/</span>
            <span className="text-gray-900 font-semibold capitalize">{activeTab}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="text-xs font-semibold text-[#082f6d] hover:bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 transition-colors"
            >
              ← Back to site
            </button>
            <span className="text-xs bg-green-50 text-green-700 font-semibold px-2.5 py-1 rounded-full border border-green-200 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
              Live Session
            </span>
          </div>
        </header>

        <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
          {activeTab === "overview" && <OverviewTab setActiveTab={setActiveTab} />}
          {activeTab === "businesses" && <BusinessesTab />}
          {activeTab === "events" && <EventsTab />}
          {activeTab === "submissions" && <SubmissionsTab />}
          {activeTab === "subscribers" && <SubscribersTab />}
          {activeTab === "deals" && <DealsTab />}
          {activeTab === "experiences" && <ExperiencesTab />}
          {activeTab === "influencers" && <InfluencersTab />}
          {activeTab === "models" && <ModelsTab />}
          {activeTab === "jobs" && <JobsTab />}
          {activeTab === "categories" && <CategoriesTab />}
          {activeTab === "export" && <RestaurantCSVExport />}
        </div>
      </main>
    </div>
  );
}

// Reusable approve / hide / delete actions for a submission row.
// `status` lives in the row's data: "approved" shows publicly, "hidden"/"rejected"
// is suppressed, anything else is treated as pending.
function StatusActions({
  item,
  onUpdate,
  onDelete,
}: {
  item: any;
  onUpdate: (id: string, patch: Record<string, any>) => Promise<void> | void;
  onDelete: (id: string) => void;
}) {
  const status = item.status || "pending";
  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => onUpdate(item.id, { status: status === "approved" ? "pending" : "approved" })}
        className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-all ${
          status === "approved"
            ? "bg-green-50 border-green-200 text-green-700"
            : "bg-white border-gray-200 text-gray-500 hover:bg-green-50 hover:text-green-700"
        }`}
        title="Approve — show on public site"
      >
        {status === "approved" ? "✓ Approved" : "Approve"}
      </button>
      <button
        onClick={() => onUpdate(item.id, { status: status === "hidden" ? "pending" : "hidden" })}
        className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-all ${
          status === "hidden"
            ? "bg-gray-200 border-gray-300 text-gray-700"
            : "bg-white border-gray-200 text-gray-500 hover:bg-gray-100"
        }`}
        title="Hide from public site"
      >
        {status === "hidden" ? "Hidden" : "Hide"}
      </button>
      <button
        onClick={() => onDelete(item.id)}
        className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600 transition-colors"
        title="Delete permanently"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function statusPillClass(status: string) {
  if (status === "approved") return "bg-green-50 text-green-700 border-green-200";
  if (status === "hidden" || status === "rejected") return "bg-gray-100 text-gray-500 border-gray-200";
  return "bg-amber-50 text-amber-700 border-amber-200";
}

// Sidebar Link Component
function SidebarLink({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
        active
          ? "bg-[#f59e0b] text-white shadow-md shadow-[#f59e0b]/10"
          : "text-blue-100 hover:text-white hover:bg-blue-900/30"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

/* ==========================================
   TAB 8b: MODELS (applications + hire requests)
   ========================================== */
function ModelsTab() {
  const apps = usePreviewSafeData("model_applications");
  const hires = usePreviewSafeData("model_hire_requests");

  const handleStatus = (src: any) => async (id: string, patch: Record<string, any>) => {
    await src.update(id, patch);
    src.refetch();
  };
  const handleDelete = (src: any) => async (id: string) => {
    if (window.confirm("Delete this submission permanently?")) {
      await src.remove(id);
      src.refetch();
    }
  };

  const Section = ({ title, subtitle, src }: { title: string; subtitle: string; src: any }) => (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500">{subtitle}</p>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {(src.rows || []).length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No submissions yet.</div>
        ) : (
          (src.rows || []).map((item: any) => (
            <div key={item.id} className="p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-gray-900 truncate">
                    {item.name || item.fullName || item.instagramHandle || item.handle || "Submission"}
                  </p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusPillClass(item.status || "pending")}`}>
                    {(item.status || "pending").toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate">{item.email || item.phone || ""}</p>
                {(item.message || item.bio || item.details) && (
                  <p className="text-xs text-gray-600 line-clamp-2 mt-1">{item.message || item.bio || item.details}</p>
                )}
              </div>
              <div className="shrink-0">
                <StatusActions item={item} onUpdate={handleStatus(src)} onDelete={handleDelete(src)} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-fadeIn">
      <div>
        <h2 className="text-xl font-extrabold text-gray-900">Model Submissions</h2>
        <p className="text-xs text-gray-500">Applications to join, and requests to hire models</p>
      </div>
      <Section title="Applications" subtitle="People applying to be listed as models" src={apps} />
      <Section title="Hire Requests" subtitle="Businesses requesting to hire a model" src={hires} />
    </div>
  );
}

/* ==========================================
   TAB 1: OVERVIEW
   ========================================== */
const CARD_ORDER_KEY = "dds_admin_card_order";

function OverviewTab({ setActiveTab }: { setActiveTab: (tab: string) => void }) {
  const { rows: businesses } = usePreviewSafeData("business_listings");
  const { rows: events } = usePreviewSafeData("event_listings");
  const { rows: submissions } = useData("contact_submissions", { autoFetch: true, limit: 1000 });
  const { rows: subscribers } = useData("newsletter_subscribers", { autoFetch: true, limit: 1000 });

  const defaultOrder = ["businesses", "events", "submissions", "subscribers"];

  const statsMap: Record<string, any> = {
    businesses: {
      id: "businesses",
      label: "Business Directory",
      count: businesses?.length || 0,
      subtext: "Total submitted listings",
      icon: <Briefcase className="w-6 h-6 text-[#082f6d]" />,
      color: "bg-[#082f6d]/5 border-[#082f6d]/10",
      tab: "businesses",
    },
    events: {
      id: "events",
      label: "Upcoming Events",
      count: events?.length || 0,
      subtext: "Live & submitted events",
      icon: <Calendar className="w-6 h-6 text-[#f59e0b]" />,
      color: "bg-[#f59e0b]/5 border-[#f59e0b]/10",
      tab: "events",
    },
    submissions: {
      id: "submissions",
      label: "Contact Inquiries",
      count: submissions?.length || 0,
      subtext: "User feedback & questions",
      icon: <Mail className="w-6 h-6 text-[#51ba4f]" />,
      color: "bg-[#51ba4f]/5 border-[#51ba4f]/10",
      tab: "submissions",
    },
    subscribers: {
      id: "subscribers",
      label: "Newsletter Subscribers",
      count: subscribers?.length || 0,
      subtext: "Active email audience",
      icon: <Users className="w-6 h-6 text-purple-600" />,
      color: "bg-purple-50 border-purple-100",
      tab: "subscribers",
    },
  };

  const cardItems = useMemo(
    () => defaultOrder.map((key) => statsMap[key]),
    [businesses?.length, events?.length, submissions?.length, subscribers?.length],
  );

  const { orderedItems, dragId, dragOverId, getDragProps } = useDragSort(CARD_ORDER_KEY, cardItems);

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#082f6d] to-blue-900 rounded-2xl p-6 md:p-8 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-lg shadow-blue-950/10">
        <div className="space-y-1.5">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Dallas Desi Scoop</h2>
          <p className="text-blue-100 text-sm max-w-[600px]">
            Welcome back, Admin! Use this control panel to instantly manage businesses, approve upcoming events, read contact submissions, and monitor newsletter subscriptions.
          </p>
        </div>
        <div className="flex gap-3 shrink-0">
          <button
            onClick={() => setActiveTab("businesses")}
            className="bg-white/10 hover:bg-white/20 text-white font-semibold text-xs px-4 py-2.5 rounded-lg transition-all border border-white/10"
          >
            Review Directory
          </button>
          <button
            onClick={() => setActiveTab("events")}
            className="bg-[#f59e0b] hover:bg-[#d97706] text-white font-bold text-xs px-4 py-2.5 rounded-lg transition-all shadow-md shadow-[#f59e0b]/10"
          >
            Approve Events
          </button>
        </div>
      </div>

      {/* Stats Cards — drag to reorder */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <GripVertical className="w-4 h-4 text-gray-400" />
          <p className="text-xs text-gray-400 font-medium">Drag cards to rearrange — your order is saved automatically</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {orderedItems.map((stat) => (
            <div
              key={stat.id}
              {...getDragProps(stat.id)}
              onClick={() => setActiveTab(stat.tab)}
              className={`bg-white border rounded-xl p-6 flex items-start justify-between cursor-pointer hover:shadow-md transition-all duration-200 group relative ${
                dragId === stat.id
                  ? "opacity-40 border-dashed border-gray-400"
                  : dragOverId === stat.id
                  ? "border-[#f59e0b] shadow-lg shadow-[#f59e0b]/10 scale-[1.02]"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              {/* Drag handle */}
              <div
                className="absolute top-3 right-3 p-1 rounded-md text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-colors opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <GripVertical className="w-4 h-4" />
              </div>
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400">{stat.label}</p>
                <h3 className="text-3xl font-extrabold text-gray-900 leading-none">{stat.count}</h3>
                <p className="text-xs text-gray-500">{stat.subtext}</p>
              </div>
              <div className={`p-3 rounded-xl border ${stat.color}`}>{stat.icon}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Tips / Guide */}
      <div className="bg-white border border-gray-100 rounded-xl p-6">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#082f6d]" /> Content Moderation Workflow
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 space-y-1.5">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center">1</span>
              User Submissions
            </h4>
            <p className="text-xs leading-relaxed">
              When users submit a new business or event using the public "Add Business" or "Add Event" forms, they are saved to the database instantly.
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 space-y-1.5">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center">2</span>
              Moderator Approval
            </h4>
            <p className="text-xs leading-relaxed">
              Navigate to the tabs on the left to inspect submitted listings. You can instantly modify metadata, delete spam, or mark items as featured.
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 space-y-1.5">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center">3</span>
              Live Updates
            </h4>
            <p className="text-xs leading-relaxed">
              Any changes you perform here will update the live website immediately without requiring a redeployment or restart.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==========================================
   TAB 2: BUSINESS LISTINGS
   ========================================== */
function BusinessesTab() {
  const { rows, update, remove, refetch } = usePreviewSafeData("business_listings");
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this business listing?")) {
      await remove(id);
      refetch();
    }
  };

  const handleToggleFeatured = async (id: string, current: boolean) => {
    await update(id, { isFeatured: !current });
    refetch();
  };


  const handleToggleStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === "approved" ? "pending" : "approved";
    await update(id, { status: newStatus });
    refetch();
  };
  const filtered = (rows || []).filter((item: any) => {
    const matchesSearch =
      (item.businessName || item.name)?.toLowerCase().includes(search.toLowerCase()) ||
      item.category?.toLowerCase().includes(search.toLowerCase()) ||
      (item.location || item.city)?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = filterCategory === "all" || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = Array.from(new Set((rows || []).map((b: any) => b.category).filter(Boolean)));

  // Drag-and-drop reordering (pointer-event based — works in sandboxed iframes)
  const {
    orderedItems,
    dragId,
    dragOverId,
    getDragProps,
  } = useDragSort("dds_admin_order_businesses", filtered);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-extrabold text-gray-900">Business Listings</h2>
          <p className="text-xs text-gray-500">Manage all registered businesses and directories</p>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search businesses by name, category, or city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-[#082f6d] focus:outline-none"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3.5 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none"
        >
          <option value="all">All Categories</option>
          {categories.map((cat: any) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Drag hint */}
      <div className="flex items-center gap-2">
        <GripVertical className="w-4 h-4 text-gray-400" />
        <p className="text-xs text-gray-400 font-medium">Drag rows to reorder — your order is saved automatically</p>
      </div>

      {/* Directory Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500">
                <th className="px-3 py-4 w-10"></th>
                <th className="px-6 py-4">Business Info</th>
                <th className="px-6 py-4">Contact</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Location</th>
                <th className="px-6 py-4">Status / Featured</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {orderedItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                    No business listings found matching filters.
                  </td>
                </tr>
              ) : (
                orderedItems.map((item: any) => (
                  <tr
                    key={item._id}
                    {...getDragProps(item._id)}
                    className={`hover:bg-gray-50/50 transition-colors group ${
                      dragId === item._id
                        ? "opacity-40"
                        : dragOverId === item._id
                        ? "bg-amber-50/50 border-t-2 border-[#f59e0b]"
                        : ""
                    }`}
                  >
                    <td className="px-3 py-4">
                      <div className="p-1 rounded-md text-gray-300 group-hover:text-gray-500 group-hover:bg-gray-100 transition-colors cursor-grab active:cursor-grabbing">
                        <GripVertical className="w-4 h-4" />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {item.imageUrl || item.image ? (
                          <img src={item.imageUrl || item.image} className="w-10 h-10 rounded-lg object-cover bg-gray-100" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-blue-50 text-[#082f6d] font-bold flex items-center justify-center">
                            {(item.businessName || item.name)?.charAt(0)}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-900">{item.businessName || item.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-xs text-gray-600">{item.contact || item.phone || item.email || "No contact info"}</p>
                      <p className="text-xs text-gray-500">{item.contactNumber || "—"}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="bg-blue-50 text-[#082f6d] text-xs font-semibold px-2.5 py-1 rounded-full border border-blue-100">
                        {item.eventCategory || item.category || "General"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <MapPin className="w-3.5 h-3.5 shrink-0" />
                        <span>
                          {item.location || item.city || "Dallas"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleStatus(item._id, item.status || "pending")}
                          className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-all ${
                            (item.status || "pending") === "approved"
                              ? "bg-green-50 border-green-200 text-green-700 shadow-sm"
                              : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {(item.status || "pending") === "approved" ? (
                            <><CheckCircle className="w-3 h-3 inline mr-1" /> Approved</>
                          ) : (
                            "Approve"
                          )}
                        </button>
                        <button
                          onClick={() => handleToggleFeatured(item._id, !!item.isFeatured)}
                          className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-all ${
                            item.isFeatured
                              ? "bg-amber-50 border-amber-200 text-amber-700 shadow-sm"
                              : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {item.isFeatured ? "★ Featured" : "☆ Make Featured"}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {item.website && (
                          <a
                            href={item.website}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          onClick={() => handleDelete(item._id)}
                          className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ==========================================
   TAB 3: EVENTS
   ========================================== */
function EventsTab() {
  const { rows, update, remove, refetch } = usePreviewSafeData("event_listings");
  const [search, setSearch] = useState("");

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this event listing?")) {
      await remove(id);
      refetch();
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === "approved" ? "pending" : "approved";
    await update(id, { status: newStatus });
    refetch();
  };

  const filtered = (rows || []).filter((item: any) => {
    return (
      (item.eventName || item.title)?.toLowerCase().includes(search.toLowerCase()) ||
      item.category?.toLowerCase().includes(search.toLowerCase()) ||
      (item.location || item.venueName)?.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-xl font-extrabold text-gray-900">Upcoming Events</h2>
        <p className="text-xs text-gray-500">Moderate, approve, and manage community event submissions</p>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search events by title, venue, category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-[#082f6d] focus:outline-none"
          />
        </div>
      </div>

      {/* Directory Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500">
                <th className="px-6 py-4">Event Details</th>
                <th className="px-6 py-4">Date & Time</th>
                <th className="px-6 py-4">Venue</th>
                <th className="px-6 py-4">Moderation Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-400">
                    No event listings found matching filters.
                  </td>
                </tr>
              ) : (
                filtered.map((item: any) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {item.image ? (
                          <img src={item.imageUrl || item.image} className="w-10 h-10 rounded-lg object-cover bg-gray-100" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-amber-50 text-[#f59e0b] font-bold flex items-center justify-center">
                            {item.title?.charAt(0)}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-900">{item.eventName || item.title}</p>
                          <p className="text-xs bg-gray-100 text-gray-600 font-semibold px-2 py-0.5 rounded w-max mt-1">
                            {item.eventCategory || item.category || "General"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-0.5 text-xs text-gray-600">
                        <p className="font-medium text-gray-900">{item.date || "TBD"}</p>
                        <p className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>{item.time || "TBD"}</span>
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-0.5 text-xs text-gray-600">
                        <p className="font-medium text-gray-900">{item.location || item.venueName || "Online"}</p>
                        <p>{item.address || ""}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleStatus(item.id, item.status)}
                        className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-all flex items-center gap-1.5 ${
                          item.status === "approved"
                            ? "bg-green-50 border-green-200 text-green-700"
                            : "bg-amber-50 border-amber-200 text-amber-700"
                        }`}
                      >
                        {item.status === "approved" ? (
                          <>
                            <CheckCircle className="w-3.5 h-3.5" /> Approved
                          </>
                        ) : (
                          <>
                            <Clock className="w-3.5 h-3.5" /> Pending Approval
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {item.ticketUrl && (
                          <a
                            href={item.ticketUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ==========================================
   PENDING APPROVAL — business + event submissions
   awaiting moderation. Approving/rejecting flips
   `status` on the same records that power the
   Business Listings / Event Listings tabs, so an
   approved item "moves" there automatically.
   ========================================== */
function PendingApprovalSection() {
  const { rows: businessRows, update: updateBusiness, refetch: refetchBusinesses } = usePreviewSafeData<any>("business_listings");
  const { rows: eventRows, update: updateEvent, refetch: refetchEvents } = usePreviewSafeData<any>("event_listings");

  const isPending = (item: any) => {
    const status = item.status || "pending";
    return status !== "approved" && status !== "rejected" && status !== "hidden";
  };

  const pendingBusinesses = (businessRows || []).filter(isPending);
  const pendingEvents = (eventRows || []).filter(isPending);
  const pendingCount = pendingBusinesses.length + pendingEvents.length;

  const getSubmittedDate = (item: any) =>
    item.submittedAt || item.createdAt
      ? new Date(item.submittedAt || item.createdAt).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "Unknown date";

  const handleApproveBusiness = async (id: string) => {
    await updateBusiness(id, { status: "approved" });
    refetchBusinesses();
  };
  const handleRejectBusiness = async (id: string) => {
    await updateBusiness(id, { status: "rejected" });
    refetchBusinesses();
  };
  const handleApproveEvent = async (id: string) => {
    await updateEvent(id, { status: "approved" });
    refetchEvents();
  };
  const handleRejectEvent = async (id: string) => {
    await updateEvent(id, { status: "rejected" });
    refetchEvents();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-extrabold text-gray-900">Pending Approval</h2>
          <p className="text-xs text-gray-500">New business and event submissions waiting to be reviewed</p>
        </div>
        <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
          {pendingCount} pending
        </span>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
        {pendingCount === 0 ? (
          <div className="p-10 text-center text-gray-400 text-sm">
            Nothing waiting on review — all caught up.
          </div>
        ) : (
          <>
            {pendingBusinesses.map((item: any) => (
              <div key={`biz-${item._id}`} className="p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 text-[#082f6d] flex items-center justify-center shrink-0">
                    <Briefcase className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold text-gray-900 truncate">{item.businessName || item.eventName || item.name || "Untitled Business"}</h4>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-[#082f6d] border border-blue-100 shrink-0">
                        Business
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 shrink-0 uppercase tracking-wide">
                        {item.status || "New"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Submitted {getSubmittedDate(item)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleApproveBusiness(item._id)}
                    className="flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 transition-colors"
                  >
                    <CheckCircle className="w-3.5 h-3.5" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleRejectBusiness(item._id)}
                    className="flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Reject
                  </button>
                </div>
              </div>
            ))}
            {pendingEvents.map((item: any) => (
              <div key={`evt-${item._id}`} className="p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-amber-50 text-[#f59e0b] flex items-center justify-center shrink-0">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold text-gray-900 truncate">{item.eventName || item.title || "Untitled Event"}</h4>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-[#f59e0b] border border-amber-100 shrink-0">
                        Event
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 shrink-0 uppercase tracking-wide">
                        {item.status || "New"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Submitted {getSubmittedDate(item)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleApproveEvent(item._id)}
                    className="flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 transition-colors"
                  >
                    <CheckCircle className="w-3.5 h-3.5" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleRejectEvent(item._id)}
                    className="flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/* ==========================================
   TAB 4: CONTACT SUBMISSIONS
   ========================================== */
function SubmissionsTab() {
  // The public Contact form writes to the "contact_submissions" table (see ContactUs.tsx).
  const { rows, update, remove, refetch } = useData("contact_submissions", { autoFetch: true });
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);

  const handleStatus = async (id: string, patch: Record<string, any>) => {
    await update(id, patch);
    refetch();
  };

  const handleDelete = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (window.confirm("Are you sure you want to delete this submission?")) {
      await remove(id);
      if (selectedSubmission?.id === id) {
        setSelectedSubmission(null);
      }
      refetch();
    }
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      <PendingApprovalSection />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* List Panel */}
      <div className="lg:col-span-2 space-y-4">
        <div>
          <h2 className="text-xl font-extrabold text-gray-900">Inquiries & Contact Submissions</h2>
          <p className="text-xs text-gray-500">Read and respond to community messages</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
          {(rows || []).length === 0 ? (
            <div className="p-12 text-center text-gray-400">No contact submissions received yet.</div>
          ) : (
            (rows || []).map((sub: any) => (
              <div
                key={sub.id}
                onClick={() => setSelectedSubmission(sub)}
                className={`p-5 cursor-pointer transition-all flex items-start justify-between gap-4 ${
                  selectedSubmission?.id === sub.id ? "bg-blue-50/50 border-l-4 border-[#082f6d]" : "hover:bg-gray-50"
                }`}
              >
                <div className="space-y-1.5 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-gray-900 truncate">{sub.name}</h4>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${statusPillClass(sub.status || "pending")}`}>
                      {(sub.status || "pending").toUpperCase()}
                    </span>
                    <span className="text-[10px] bg-gray-100 text-gray-500 font-semibold px-2 py-0.5 rounded-full shrink-0">
                      {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : (sub.createdAt ? new Date(sub.createdAt).toLocaleDateString() : "")}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-gray-500 truncate">{sub.email}</p>
                  <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">{sub.message}</p>
                  <div onClick={(e) => e.stopPropagation()} className="pt-1">
                    <StatusActions item={sub} onUpdate={handleStatus} onDelete={(id) => handleDelete(id)} />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Detail Viewer Panel */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 h-max sticky top-24 space-y-5">
        {selectedSubmission ? (
          <div className="space-y-5">
            <div className="border-b border-gray-100 pb-4">
              <h3 className="font-bold text-gray-900 text-base">{selectedSubmission.name}</h3>
              <a href={`mailto:${selectedSubmission.email}`} className="text-xs text-blue-600 hover:underline">
                {selectedSubmission.email}
              </a>
              {selectedSubmission.phone && <p className="text-xs text-gray-500 mt-0.5">{selectedSubmission.phone}</p>}
            </div>

            <div className="space-y-1.5">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Inquiry Message</h4>
              <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-100 whitespace-pre-wrap leading-relaxed">
                {selectedSubmission.message}
              </p>
            </div>

            <a
              href={`mailto:${selectedSubmission.email}?subject=Response from Dallas Desi Scoop`}
              className="w-full bg-[#082f6d] hover:bg-blue-950 text-white font-bold py-2.5 px-4 rounded-lg transition-colors text-xs flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" />
              <span>Reply via Email</span>
            </a>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400 space-y-2">
            <Mail className="w-8 h-8 mx-auto text-gray-300" />
            <p className="text-xs">Select a submission from the list to view full details.</p>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

/* ==========================================
   TAB 5: SUBSCRIBERS
   ========================================== */
function SubscribersTab() {
  const { rows, remove, refetch } = useData("newsletter_subscribers", { autoFetch: true });

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to remove this subscriber?")) {
      await remove(id);
      refetch();
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-xl font-extrabold text-gray-900">Newsletter Subscribers</h2>
        <p className="text-xs text-gray-500">View and export subscribers to your platform newsletter</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500">
                <th className="px-6 py-4">Subscriber Email</th>
                <th className="px-6 py-4">Subscription Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {(rows || []).length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center text-gray-400">
                    No subscribers found.
                  </td>
                </tr>
              ) : (
                (rows || []).map((item: any) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-gray-900">{item.email}</td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {item.subscribedAt ? new Date(item.subscribedAt).toLocaleString() : "TBD"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ==========================================
   TAB 6: DEALS
   ========================================== */
function DealsTab() {
  // Public deal-submission forms write to three tables (see Deals.tsx).
  const business = useData("business_deals", { autoFetch: true });
  const event = useData("event_deals", { autoFetch: true });
  const community = useData("community_deals", { autoFetch: true });

  const all = [
    ...(business.rows || []).map((r: any) => ({ ...r, _src: business, dealType: "Business" })),
    ...(event.rows || []).map((r: any) => ({ ...r, _src: event, dealType: "Event" })),
    ...(community.rows || []).map((r: any) => ({ ...r, _src: community, dealType: "Community" })),
  ];

  const handleStatus = async (item: any, patch: Record<string, any>) => {
    await item._src.update(item.id, patch);
    item._src.refetch();
  };
  const handleDelete = async (item: any) => {
    if (window.confirm("Are you sure you want to delete this deal?")) {
      await item._src.remove(item.id);
      item._src.refetch();
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-xl font-extrabold text-gray-900">Deals & Offers</h2>
        <p className="text-xs text-gray-500">Submitted promotional deals — approve to publish, hide, or delete</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500">
                <th className="px-6 py-4">Deal Title</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Business</th>
                <th className="px-6 py-4">Discount</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {all.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-400">
                    No deals submitted yet. They appear here when users submit via the Deals page.
                  </td>
                </tr>
              ) : (
                all.map((item: any) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-gray-900">{item.title || item.dealTitle || "Untitled"}</td>
                    <td className="px-6 py-4">
                      <span className="bg-blue-50 text-[#082f6d] text-xs font-semibold px-2.5 py-1 rounded-full border border-blue-100">
                        {item.dealType}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{item.businessName || item.name || "General"}</td>
                    <td className="px-6 py-4">
                      <span className="bg-green-50 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full border border-green-100">
                        {item.discountCode || item.discount || "Promo"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <StatusActions
                        item={item}
                        onUpdate={(_id, patch) => handleStatus(item, patch)}
                        onDelete={() => handleDelete(item)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ==========================================
   TAB 8: INFLUENCERS (applications + hire requests)
   ========================================== */
/* ==========================================
   TAB: JOBS (job postings from the /jobs page)
   Approving a posting makes it appear in the
   "Looking for a Job" listings on /jobs.
   ========================================== */
function JobsTab() {
  const jobs = usePreviewSafeData("job_postings");

  const handleStatus = async (id: string, patch: Record<string, any>) => {
    await jobs.update(id, patch);
    jobs.refetch();
  };
  const handleDelete = async (id: string) => {
    if (window.confirm("Delete this job posting permanently?")) {
      await jobs.remove(id);
      jobs.refetch();
    }
  };

  const rows = jobs.rows || [];
  const pendingCount = rows.filter(
    (item: any) => (item.status || "pending") !== "approved" && (item.status || "pending") !== "hidden" && (item.status || "pending") !== "rejected"
  ).length;

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-extrabold text-gray-900">Job Postings</h2>
          <p className="text-xs text-gray-500">
            Submitted from the Jobs page — approve to show in the public listings
          </p>
        </div>
        {pendingCount > 0 && (
          <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            {pendingCount} pending
          </span>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {rows.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No job postings yet.</div>
        ) : (
          rows.map((item: any) => (
            <div key={item.id} className="p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-gray-900 truncate">
                    {item.jobTitle || "Job Posting"}
                  </p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusPillClass(item.status || "pending")}`}>
                    {(item.status || "pending").toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {[item.companyName, item.jobType, item.location, item.workArrangement]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {item.jobCategory && (
                  <p className="text-xs text-gray-400 mt-0.5">{item.jobCategory}</p>
                )}
                {item.jobDescription && (
                  <p className="text-xs text-gray-600 line-clamp-2 mt-1">{item.jobDescription}</p>
                )}
                {item.howToApply && (
                  <p className="text-xs text-gray-500 line-clamp-1 mt-1">
                    <span className="font-semibold">Apply: </span>
                    {item.howToApply}
                  </p>
                )}
                {item.companyWebsite && (
                  <a
                    href={item.companyWebsite.startsWith("http") ? item.companyWebsite : `https://${item.companyWebsite}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#082f6d] hover:underline mt-1 inline-block"
                  >
                    {item.companyWebsite}
                  </a>
                )}
              </div>
              <div className="shrink-0">
                <StatusActions item={item} onUpdate={handleStatus} onDelete={handleDelete} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function InfluencersTab() {
  const apps = usePreviewSafeData("influencer_applications");
  const hires = usePreviewSafeData("influencer_hire_requests");

  const handleStatus = (src: any) => async (id: string, patch: Record<string, any>) => {
    await src.update(id, patch);
    src.refetch();
  };
  const handleDelete = (src: any) => async (id: string) => {
    if (window.confirm("Delete this submission permanently?")) {
      await src.remove(id);
      src.refetch();
    }
  };

  const Section = ({ title, subtitle, src }: { title: string; subtitle: string; src: any }) => (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500">{subtitle}</p>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {(src.rows || []).length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No submissions yet.</div>
        ) : (
          (src.rows || []).map((item: any) => (
            <div key={item.id} className="p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-gray-900 truncate">
                    {item.name || item.fullName || item.instagramHandle || item.handle || "Submission"}
                  </p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusPillClass(item.status || "pending")}`}>
                    {(item.status || "pending").toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate">{item.email || item.phone || ""}</p>
                {(item.message || item.bio || item.details) && (
                  <p className="text-xs text-gray-600 line-clamp-2 mt-1">{item.message || item.bio || item.details}</p>
                )}
              </div>
              <div className="shrink-0">
                <StatusActions item={item} onUpdate={handleStatus(src)} onDelete={handleDelete(src)} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-fadeIn">
      <div>
        <h2 className="text-xl font-extrabold text-gray-900">Influencer Submissions</h2>
        <p className="text-xs text-gray-500">Applications to join, and requests to hire influencers</p>
      </div>
      <Section title="Applications" subtitle="People applying to be listed as influencers" src={apps} />
      <Section title="Hire Requests" subtitle="Businesses requesting to hire an influencer" src={hires} />
    </div>
  );
}

/* ==========================================
   TAB 7: EXPERIENCES
   ========================================== */
function normalizeExperienceRow(r: any) {
  if (r && typeof r === "object" && r.data && typeof r.data === "object") {
    return { ...r.data, _id: r._id || r.data._id, id: r.id || r._id || r.data._id, createdAt: r.createdAt || r.data.createdAt, status: r.status || r.data.status };
  }
  return r;
}

// The real backend's PUT (update) endpoint only allows a row to be modified
// by the exact session that created it ("Not authorized to modify this
// row" / "Valid session required for updates" — confirmed directly against
// the live API). A submitted experience is created by the VISITOR's
// session; Approve/Reject is clicked from the ADMIN's session — so that
// update can never succeed cross-session. It was silently falling back to
// a local-only buffer, so "✓ Approved" only ever showed in the admin's own
// browser while the real row stayed "pending" forever — invisible on the
// public page in any other session. INSERT has no such restriction, so
// moderation decisions are stored as independent marker rows.
//
// IMPORTANT: those markers live in the SAME `experiences` table, not a
// separate `experience_moderation` table. Confirmed directly against the
// live API: `experience_moderation` has public read permanently disabled
// ("Public read access is not allowed for this table") — even right after
// explicitly re-provisioning it with allowPublicRead: true. Any reader in a
// different session (including Admin re-opened fresh, and definitely the
// public Experiences page) gets that 403 and sees zero moderation rows, no
// matter what was inserted. `experiences` itself DOES allow public reads
// from any session (verified with a brand-new random session id), so
// moderation decisions are stored there as tagged marker rows instead:
// { source: "moderation-marker", targetExperienceId, status }.
// Effective status = latest moderation marker for that experience id (or
// the row's own status/"pending" if none exists yet).
const MODERATION_MARKER_SOURCE = "moderation-marker";
// Real DELETE always fails cross-session too — confirmed directly against the
// live API ("Valid session required for deletions"), same constraint as
// UPDATE. The backend only accepts a session for delete/update if it EXACTLY
// matches the server-issued session token from the moment that row was
// created — the shared deterministic string used for reads/inserts is never
// accepted for delete, even on rows that were themselves inserted with it.
// So `remove(id)` on this table can never really delete anything; it just
// silently local-tombstones in whichever browser clicked it. Fix: use the
// SAME insert-only marker pattern as Approve/Reject (inserts are always
// allowed, no session ownership check) — a "deletion-marker" row is
// inserted and both Admin and the public page filter out any row with a
// matching marker. This is real, permanent, network-wide removal.
const DELETION_MARKER_SOURCE = "deletion-marker";

function latestModerationStatus(markerRows: any[], experienceId: string): string | null {
  const matches = markerRows
    .filter((m: any) => m.source === MODERATION_MARKER_SOURCE && String(m.location) === String(experienceId))
    .sort((a: any, b: any) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  return matches.length ? matches[0].status : null;
}

function isDeleted(deletionMarkerRows: any[], experienceId: string): boolean {
  return deletionMarkerRows.some((m: any) => String(m.location) === String(experienceId));
}

function ExperiencesTab() {
  const { rows, insert: insertMarker, refetch } = usePreviewSafeData("experiences");
  const [filter, setFilter] = useState<"pending" | "approved" | "rejected" | "all">("pending");
  // Sandboxed preview iframes can silently block/no-op window.confirm(),
  // which made Delete look completely dead (no error, no request fired).
  // Use an in-UI confirm step instead of the native browser dialog.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [moderatingId, setModeratingId] = useState<string | null>(null);

  const normalized = (rows || []).map(normalizeExperienceRow);
  // Marker rows are internal bookkeeping (moderation + deletion decisions),
  // never real submissions — keep them out of the submissions list entirely.
  const markerRows = normalized.filter((r: any) => r.source === MODERATION_MARKER_SOURCE);
  const deletionMarkerRows = normalized.filter((r: any) => r.source === DELETION_MARKER_SOURCE);
  const allRows = normalized
    .filter((r: any) => r.source !== MODERATION_MARKER_SOURCE && r.source !== DELETION_MARKER_SOURCE)
    .filter((r: any) => !isDeleted(deletionMarkerRows, r._id || r.id));
  const getStatus = (item: any) => {
    const id = item._id || item.id;
    return latestModerationStatus(markerRows, id) || item.status || "pending";
  };
  const filteredRows = allRows.filter((item: any) => filter === "all" || getStatus(item) === filter);
  const pendingCount = allRows.filter((item: any) => getStatus(item) === "pending").length;

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      // Insert-only marker (see DELETION_MARKER_SOURCE) — real, permanent,
      // works regardless of which session created the original row.
      await insertMarker({
        title: "__deletion_marker__",
        experience: "__deletion_marker__",
        source: DELETION_MARKER_SOURCE,
        location: id,
        createdAt: new Date().toISOString(),
      });
      await refetch();
    } finally {
      setDeletingId(null);
      setPendingDeleteId(null);
    }
  };

  const setModeration = async (id: string, status: "approved" | "rejected") => {
    setModeratingId(id);
    try {
      // Marker row: needs the same required fields (title/experience) as a
      // real submission to pass backend validation, tagged so it's filtered
      // out of every submissions list (see allRows/markerRows above).
      await insertMarker({
        title: "__moderation_marker__",
        experience: "__moderation_marker__",
        source: MODERATION_MARKER_SOURCE,
        location: id,
        status,
        createdAt: new Date().toISOString(),
      });
      await refetch();
    } finally {
      setModeratingId(null);
    }
  };

  const handleApprove = (id: string) => setModeration(id, "approved");
  const handleReject = (id: string) => setModeration(id, "rejected");

  const badgeClass = (status: string) => {
    if (status === "approved") return "bg-green-50 text-green-700 border-green-200";
    if (status === "rejected") return "bg-red-50 text-red-700 border-red-200";
    return "bg-amber-50 text-amber-700 border-amber-200";
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-xl font-extrabold text-gray-900">Community Experience Submissions</h2>
        <p className="text-xs text-gray-500">New submissions land here as Pending and stay hidden from the public site until you Approve them.</p>
      </div>

      <div className="flex gap-2">
        {(["pending", "approved", "rejected", "all"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${
              filter === f ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {f}
            {f === "pending" && pendingCount > 0 ? ` (${pendingCount})` : ""}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500">
                <th className="px-6 py-4">Title</th>
                <th className="px-6 py-4">Author</th>
                <th className="px-6 py-4">Location</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-400">
                    No {filter !== "all" ? filter : ""} experience submissions.
                  </td>
                </tr>
              ) : (
                filteredRows.map((item: any) => {
                  const status = getStatus(item);
                  const id = item._id || item.id;
                  return (
                    <tr key={id} className="hover:bg-gray-50/50 transition-colors align-top">
                      <td className="px-6 py-4 font-semibold text-gray-900 max-w-[280px]">
                        <div>{item.title || item.eventName || "Untitled"}</div>
                        {item.experience && (
                          <div className="text-xs text-gray-500 font-normal mt-1 line-clamp-2">{item.experience}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">{item.submittedName || item.author || "Anonymous"}</td>
                      <td className="px-6 py-4 text-xs text-gray-500">{item.location || "—"}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border capitalize ${badgeClass(status)}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        {status !== "approved" && (
                          <button
                            onClick={() => handleApprove(id)}
                            className="p-1.5 hover:bg-green-50 rounded-lg text-gray-400 hover:text-green-600 transition-colors"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        {status !== "rejected" && (
                          <button
                            onClick={() => handleReject(id)}
                            className="p-1.5 hover:bg-amber-50 rounded-lg text-gray-400 hover:text-amber-600 transition-colors"
                            title="Reject"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                        {pendingDeleteId === id ? (
                          <span className="inline-flex items-center gap-1.5 align-middle">
                            <button
                              onClick={() => handleDelete(id)}
                              disabled={deletingId === id}
                              className="text-[11px] font-bold px-2 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 transition-colors"
                            >
                              {deletingId === id ? "Deleting…" : "Confirm"}
                            </button>
                            <button
                              onClick={() => setPendingDeleteId(null)}
                              disabled={deletingId === id}
                              className="text-[11px] font-semibold px-2 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            onClick={() => setPendingDeleteId(id)}
                            className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
